import { ref, type VNode } from 'vue';

// Imperative confirm / alert / prompt facade. Built on ElDialog so modal
// positioning / overlay / esc / focus-trap come for free; inner markup is
// custom for the product confirm style (no X close button, borderless cancel,
// filled-warning icon left of title).
//
// Singleton via reactive ref: <MessageAlertHost> lives once at the App
// root and reads this state. Service callers don't manage mounting.

export type MessageAlertType = 'alert' | 'confirm' | 'prompt';
export type MessageAlertTheme = 'info' | 'danger';

export interface MessageAlertOptions {
  title: string;
  // A plain string, or a VNode when part of the sentence needs its own markup
  // (an emphasised value, a link). Deliberately NOT an HTML string — the host
  // renders a VNode directly, so nothing is ever parsed as markup.
  content?: string | VNode;
  type?: MessageAlertType;
  theme?: MessageAlertTheme;
  cancelText?: string;
  confirmText?: string;
  placeholder?: string;
  // When set, confirm stays disabled until the user types this exact string
  // in the prompt input; used for dangerous delete confirmations.
  checkInput?: string;
  // Opt-in async confirm. When set, clicking confirm runs this and keeps the
  // dialog open + button loading until it settles; on reject the dialog stays
  // open (the host swallows it — error surfacing is this callback's job, e.g.
  // the axios interceptor toast). Omit for the plain click-to-close gate.
  onConfirm?: () => Promise<void>;
}

interface PendingAlert {
  options: MessageAlertOptions;
  resolve: (value: string) => void;
  reject: (reason?: unknown) => void;
}

export const messageAlertState = ref<PendingAlert | null>(null);

function show(options: MessageAlertOptions): Promise<string> {
  return new Promise((resolve, reject) => {
    messageAlertState.value = { options, resolve, reject };
  });
}

export const messageAlert = {
  alert(options: Omit<MessageAlertOptions, 'type'>): Promise<void> {
    return show({ ...options, type: 'alert' }).then(() => undefined);
  },
  confirm(options: Omit<MessageAlertOptions, 'type'>): Promise<void> {
    return show({ ...options, type: 'confirm' }).then(() => undefined);
  },
  prompt(options: Omit<MessageAlertOptions, 'type'>): Promise<string> {
    return show({ ...options, type: 'prompt' });
  },
};
